import { DialogContent } from '@mui/material';
import { find } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FormContainer, useForm } from 'react-hook-form-mui';

import { useFetch } from 'hooks';
import { translate } from 'utils';
import { ATTESTATION_FORM_STEPS } from 'utils/constants';

import { ModalHeader } from 'components/_commons/Modals/_ModalHeader';
import { ClassifyDocumentFormContainer } from 'components/_commons/Modals/ClassifyDocument/ClassifyDocumentFormContainer';
import { DocumentActionButtons } from 'components/_commons/Modals/DocumentVerification/DocumentActionButtons';
import { SectionEnum } from 'components/AttestationForm/attestationFormSection.enum';

import { ModalProps } from 'hooks/ModalProvider';
import {
  AttestationFormOperatorService, AttestationFormRequestService, AttestationFormToolService, DocumentService
} from 'services';
import { enumOptionTypes, EnumService, enumValueTypes } from 'services/EnumService';

export const ClassifyDocumentModal = ({
  onClose,
  onSubmit,
  canValidate,
  modalState: {
    folder, requestId, section, canEdit = true
  }
}: ModalProps<any>) => {
  const [categories, setCategories] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [options, setOptions] = useState([]);
  const [onChangeEntity, setOnChangeEntity] = useState(null);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(null);
  const [isClassifyingDocument, setIsClassifyingDocument] = useState(false);
  const { response: fileData, isLoading: loadingDocumentContent } = useFetch(
    () => DocumentService.getFileTransferModel(folder.documentId),
    null
  );

  const {
    response: allowedSections, isLoading: sectionsLoading
  } = useFetch(() => EnumService.getEnumOptions(enumOptionTypes.SECTION_DOCUMENT), []);

  const formContext = useForm<any>({
    defaultValues: {
      document: folder.document,
      section: { value: section, label: translate(find(ATTESTATION_FORM_STEPS, { section })?.label) }
    }
  });

  const onSubmitModal = useCallback((form) => {
    onSubmit(form, setIsClassifyingDocument);
  }, [onSubmit]);

  const loadOperators = useCallback(() => {
    setOptions([]);
    setLoadingOptions(true);
    AttestationFormRequestService.getOperators(requestId)
      .then((resp) => {
        setLoadingOptions(false);
        setOnChangeEntity(null);
        setOptions(resp.map((op) => ({ value: op.id, label: `${op.person?.firstName} ${op.person?.lastName}` })));
      });
  }, [requestId]);

  const loadCategories = () => {
    EnumService.getEnumValues(enumValueTypes.ATTESTATION_CATEGORY)
      .then((list) => setCategories(list.map((opt) => ({ value: opt, label: opt }))));
  };

  const loadDocumentsOperatorTypes = () => {
    AttestationFormOperatorService.getDocumentsType()
      .then((list) => setDocumentTypes(list));
  };

  const loadSectionOperatorResources = useCallback(() => {
    loadOperators();
    loadCategories();
    loadDocumentsOperatorTypes();
  }, [loadOperators]);

  const loadSectionToolResources = useCallback(() => {
    setOptions([]);
    setLoadingOptions(true);
    AttestationFormToolService.getAttestationFormTools(requestId).then((tools) => {
      if (formContext.watch('section')?.value === SectionEnum.Tooling) {
        setOptions(
          tools.map((tool) => ({
            value: tool.id,
            label: `${tool.equipmentView.fullName}`,
            type: tool.equipmentView.type.value
          }))
        );
        setOnChangeEntity(() => (e, entity) => {
          formContext.resetField('documentType');
          if (!entity) {
            setDocumentTypes([]);
            return;
          }
          setLoadingDocumentTypes(true);
          AttestationFormToolService.getAvailableDocumentTypes(entity.type)
            .then((resp) => setDocumentTypes(resp))
            .finally(() => setLoadingDocumentTypes(false));
        });
      }
    }).finally(() => setLoadingOptions(false));
  }, [formContext, requestId]);

  const loadSectionTracabilityResources = useCallback(() => {
    setOptions([]);
    setLoadingOptions(true);
    AttestationFormRequestService.getTracabilityDocuments(requestId).then((resp) => {
      setLoadingOptions(false);
      setOnChangeEntity(null);
      setOptions(resp.map((element) => element.listType));
    });
  }, [requestId]);

  useEffect(() => {
    switch (formContext.watch('section')?.value) {
    case SectionEnum.Operators:
      loadSectionOperatorResources();
      break;

    case SectionEnum.Tooling:
      loadSectionToolResources();
      break;

    case SectionEnum.Tracability:
      loadSectionTracabilityResources();
      break;

    default:
      break;
    }
    formContext.setValue('documentType', null);
    // eslint-disable-next-line
  }, [formContext.watch('section')?.value]);

  return (
    <FormContainer formContext={formContext} FormProps={{ autoComplete: 'off', name: 'classifyDocuments' }} onSuccess={onSubmitModal}>
      <ModalHeader onClose={onClose}>{translate('attestationFormRequest.classifyDocumentsModal.title')}</ModalHeader>
      <DialogContent>
        <ClassifyDocumentFormContainer
          allowedSections={allowedSections}
          allowedSectionsLoading={sectionsLoading}
          canEdit={canEdit}
          canValidate={canValidate}
          categories={categories}
          documentTypes={documentTypes}
          fileData={fileData}
          formContext={formContext}
          loadingDocumentContent={loadingDocumentContent}
          loadingDocumentTypes={loadingDocumentTypes}
          loadingOptions={loadingOptions}
          options={options}
          onChangeLinkedEntity={onChangeEntity}
        />
      </DialogContent>
      <DocumentActionButtons
          canEdit={canEdit}
          documentNumber={1}
          documentsCount={1}
          goBack={() => {}}
          isSaving={isClassifyingDocument}
      />
    </FormContainer>
  );
};
